import React, { useCallback, useState } from 'react';
import Inspector, { Band, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { RealmRings } from '../../components/nucleus/cells';
import AccessList from '../../components/nucleus/AccessList';
import { ConsentFrame, ViewTabs, PreviewPane } from '../../components/nucleus/GrantViews';
import { Section } from '../../components/grant/parts';
import FaceChoice from '../../components/grant/FaceChoice';
import { GrantMark } from '../nucleus/RealmsParts';
import { usePlace } from '../../nucleus/place';
import { spaceWords } from '../../nucleus/access';
import { kindOne } from '../nucleus/RealmsModel';
import { until } from '../../nucleus/format';
import { namesOf, recipientName } from './realmsData';
import { tokenRealms } from '../access/TokenMaker';

// ONE PRIVACY REALM: the most anything shared from it can get (its ceiling), who
// is shared from it, and how you appear to them, drawn with the consent page's
// parts. The words are the consent page's: a realm COVERS; people and apps are
// SHARED FROM it; each one's exposure profile gets at most what its realms cover,
// narrowed to what was approved. Three tabs: the permission rules, a preview of
// the nodes it covers, and the exposure profiles that use it.

const adminHintOf = (spaceName) => `Only an admin of ${spaceName || 'this group'} can change its realms`;

export const realmTag = (realm, profiles = []) => (realm.builtin ? 'Realm · Built in' : realm.system ? 'Default realm'
    : realm.shared ? `Realm · Only ${recipientName(realm, profiles)}` : 'Realm');

/** Why a built-in realm's act is off: No access, and Full Access where the
 *  admins sit, are never edited or deleted. */
export function builtinLock(realm, act) {
    if (realm.builtin === 'no_access') return `${realm.name} is built in and grants nothing.`;
    if (realm.builtin === 'full_access') return act === 'delete' ? `${realm.name} is built in.` : `${realm.name} is built in: admins sit on it, and it covers everything.`;
    return null;
}

// What each of a group's built-in realms is for, in the inspector's About.
const BUILTIN_ABOUT = {
    no_access: (space) => `Built in. A member on it sees none of ${space}’s data; they are in the group and can see who else is. It can be neither changed nor deleted.`,
    full_access: (space) => `Built in, and where ${space}’s admins sit: it covers all of ${space}’s data — read, write, use its plugins’ tools. It is also the default realm for apps that ask for everything. You can duplicate it, but not change or delete it.`,
};

const ROLE_WORD = { admin: 'Admin', member: 'Member' };

const realmsLine = (names) => (names.length ? names.join(' + ') : 'no realm');

// One realm's acts, the same in its inspector and on its cell's right-click.
// A default realm can be duplicated, never edited or deleted: those two stand
// greyed, and say why.
export function realmActionsOf(realm, { isAdmin, spaceName, inside, holding = inside, calls, edit, fork, onDeleted, toast }) {
    const adminHint = adminHintOf(spaceName);
    const actions = [];
    const noEdit = builtinLock(realm, 'edit');
    const noDelete = builtinLock(realm, 'delete');
    if (noEdit) {
        actions.push({ id: 'edit', label: 'Edit', disabled: true, hint: noEdit, run: () => {} });
    } else if (realm.system) {
        actions.push({ id: 'edit', label: 'Edit', disabled: true, hint: "You can't edit default realms", run: () => {} });
    } else {
        actions.push({
            id: 'edit', label: 'Edit', primary: true,
            disabled: !isAdmin, hint: isAdmin ? 'Change what it covers' : adminHint,
            run: () => edit(realm, false),
        });
    }
    actions.push({
        id: 'fork', label: 'Duplicate', primary: realm.system,
        disabled: !isAdmin, hint: isAdmin ? 'A new realm that covers the same' : adminHint,
        run: () => fork(realm),
    });
    if (noDelete) {
        actions.push({ id: 'delete', label: 'Delete', danger: true, disabled: true, hint: noDelete, run: () => {} });
    } else if (realm.system) {
        actions.push({ id: 'delete', label: 'Delete', danger: true, disabled: true, hint: "You can't delete default realms", run: () => {} });
    } else {
        // A realm something is still shared from cannot be deleted: that would narrow
        // (or, before, destroy) their contracts without asking. Take it off each one
        // first — Stop sharing from it, or Revoke — which the server requires too.
        // Lapsed contracts count: they can be extended, so the server counts them.
        const inUse = holding.length > 0;
        actions.push({
            id: 'delete', label: 'Delete', danger: true,
            disabled: !isAdmin || inUse,
            hint: !isAdmin ? adminHint : inUse ? stillHeldBy(realm, holding) : undefined,
            confirm: {
                title: `Delete ${realm.name}?`,
                body: 'Nothing is shared from it, so nothing else changes.',
            },
            run: async () => {
                await calls.deleteRealm(realm);
                toast.success(`${realm.name} is deleted.`);
                onDeleted();
            },
        });
    }
    return actions;
}

// What clears each contract: one shared from other realms too stops sharing from
// this one; one this is the only realm of is revoked.
function stillHeldBy(realm, holding) {
    const out = holding.filter((p) => p.realmIds.length > 1);
    const only = holding.filter((p) => p.realmIds.length <= 1);
    const parts = [];
    if (out.length) parts.push(`stop sharing ${realm.name} on ${namesOf(out)}`);
    if (only.length) parts.push(`revoke ${namesOf(only)}, which ${only.length === 1 ? 'has' : 'have'} no other realm`);
    return `${namesOf(holding)} ${holding.length === 1 ? 'is' : 'are'} still shared from it. To delete it, ${parts.join(', and ')}.`;
}

// One exposure profile shared from this realm: open it, or stop sharing it from
// here (never from its last realm: that is Revoke, on the exposure profile).
export function reachActionsOf(realm, p, { isAdmin, spaceName, openGrant, calls, toast, reload }) {
    const adminHint = adminHintOf(spaceName);
    const only = p.realmIds.length <= 1;
    const keeps = realmsLine(p.realms.filter((r) => r.id !== realm.id).map((r) => r.name));
    return [
        { id: 'grant', label: 'Open its contract', primary: true, run: () => openGrant(p) },
        {
            id: 'out', label: `Stop sharing from ${realm.name}`, danger: true,
            disabled: !isAdmin || only,
            hint: !isAdmin ? adminHint : only ? `${realm.name} is its only realm. Revoke its contract to stop sharing` : undefined,
            confirm: {
                title: `Stop sharing ${realm.name} with ${p.label}?`,
                body: `${p.label} stops getting what ${realm.name} covers. It still gets what ${keeps} cover${p.realms.length - 1 === 1 ? 's' : ''}.`,
                label: 'Stop sharing',
            },
            run: async () => {
                await calls.takeOut(realm, p);
                toast.success(`${realm.name} is no longer shared with ${p.label}.`);
                reload();
            },
        },
    ];
}

export default function RealmInspector({ realm, data, calls, actions, reachActions, onEdit, onClose }) {
    const { isAdmin, groupId, spaceName, to, navigate } = usePlace();
    const w = spaceWords(groupId, spaceName);
    const [view, setView] = useState('rules');
    const inside = data.inside(realm);
    const given = groupId && data.givenTo ? data.givenTo(realm) : [];
    const readable = realm.system || realm.builtin === 'no_access' || realm.builtin === 'full_access' || !isAdmin;
    const loadPreview = useCallback((q) => calls.realmPreview(realm, q), [calls, realm]);
    // a door to making a token on it, among what uses it: the sheet lives in Contracts,
    // where tokens are listed. Only on a realm a token can reach through, for an admin.
    const tokenDoor = isAdmin && tokenRealms([realm]).length > 0
        ? () => navigate(to('realms', 'profiles', `new=token&realm=${encodeURIComponent(realm.id)}`)) : null;

    // how it presents you, in the consent page's FaceChoice: its own identity, else your default
    const identities = data.faces.map((f) => ({
        id: f.id, name: f.name, avatar_url: f.node?.avatar_url || null, is_default: f.id === String(data.defaultFaceId || ''),
    }));
    const faceId = realm.profileNodeId ? String(realm.profileNodeId) : (data.defaultFaceId ? String(data.defaultFaceId) : null);

    return (
        <Inspector
            face={<RealmRings color={realm.color} />}
            tag={realmTag(realm, data.profiles)}
            title={realm.name}
            sub={realm.description}
            actions={actions}
            onClose={onClose}
        >
            {(realm.system || realm.shared || (groupId && BUILTIN_ABOUT[realm.builtin])) && (
                <Band title="About">
                    {groupId && BUILTIN_ABOUT[realm.builtin] && <Note>{BUILTIN_ABOUT[realm.builtin](spaceName)}</Note>}
                    {realm.nullRealm && <Note>A default realm, for apps that ask for nothing. It covers none of {w.your} data. You can duplicate it, but not change or delete it.</Note>}
                    {realm.system && !realm.nullRealm && !realm.builtin && <Note>A default realm, for apps that ask for everything. It covers all of {w.your} data, so use it rarely: a narrower realm is safer. You can duplicate it, but not change or delete it.</Note>}
                    {realm.shared && <Note>Only {recipientName(realm, data.profiles)} uses this realm. It was made when {w.inGroup ? spaceName : 'you'} shared something with them that none of {w.inGroup ? 'its' : 'your'} realms cover. It holds only that, grows with each such share, and is never added to anyone else. Duplicate it for a reusable copy.</Note>}
                </Band>
            )}
            {groupId && (
                <Band title="Members on it" aside={String(given.length)}>
                    {given.length ? (
                        <div className="nx-consent__rows">
                            {given.map((m) => (
                                <Row key={m.id} name={m.name} sub={ROLE_WORD[m.role] || 'Member'}
                                    onPress={() => navigate(to('people', null, `open=${encodeURIComponent(m.id)}`))} />
                            ))}
                        </div>
                    ) : <Note>No one is on it.</Note>}
                </Band>
            )}
            <ConsentFrame>
                <ViewTabs value={view} onChange={setView} tabs={[['rules', 'Permission rules'], ['preview', 'Preview'], ['used', `Used by (${inside.length})`]]} />
                {view === 'rules' ? (<>
                    <Section title={w.inGroup ? `How ${spaceName} appears` : 'How you appear'}>
                        {faceId ? (
                            <FaceChoice
                                face={{ id: faceId, source: realm.profileNodeId ? 'realm' : null, realmName: realm.name }}
                                identities={identities}
                                owned={identities.length}
                                appName="Whatever is shared from it"
                            />
                        ) : (
                            <div className="gp-locked">
                                <div>
                                    <div className="gp-locked__t">No identity named.</div>
                                    <div className="gp-locked__d">{w.inGroup
                                        ? `This realm names none and ${spaceName} has no default, so whatever is shared from it sees the group’s username until one is picked.`
                                        : 'This realm names none and you have no default, so whatever is shared from it sees your username until you pick one.'}</div>
                                </div>
                            </div>
                        )}
                    </Section>

                    <Section
                        title="What it covers"
                        aside={readable && <button type="button" className="gp-link" onClick={() => onEdit(realm, true)}>See in full</button>}
                    >
                        <AccessList access={realm.ceilingBlob} schemaNames={data.schemaNames} loadTitles={calls.nodeTitles}
                            empty="Nothing. Whatever is shared from it gets nothing." />
                        {realm.excludedNodeIds?.length > 0 && (
                            <p className="nx-rules__more">Never shared from it: {realm.excludedNodeIds.length === 1 ? '1 node' : `${realm.excludedNodeIds.length} nodes`} set aside.</p>
                        )}
                    </Section>
                </>) : view === 'used' ? (
                    <div className="nx-consent__pane">
                        {tokenDoor && <p className="tk__door"><button type="button" className="gp-link" onClick={tokenDoor}>Make a token on it</button></p>}
                        {inside.length === 0 && <p className="nx-preview__dead">Not used by anything yet.</p>}
                        {inside.length > 0 && <div className="nx-consent__rows">
                            {inside.map((p) => {
                                const expiring = p.expires && p.expires.getTime() < Date.now() + 14 * 86_400_000;
                                return (
                                    <Row
                                        key={p.id}
                                        lead={<GrantMark profile={p} size={34} dim={!p.active} />}
                                        name={p.label}
                                        sub={`${kindOne(p.kind)} · shared from ${realmsLine(p.realms.map((r) => r.name))}`}
                                        end={p.waiting ? 'waiting' : !p.active ? 'paused' : expiring ? `expires ${until(p.expires, { days: true })}` : undefined}
                                        endTone={!p.active || expiring ? 'warn' : undefined}
                                        onPress={() => navigate(to('realms', 'profiles', `open=${encodeURIComponent(p.key)}`))}
                                        actions={reachActions(p)}
                                    />
                                );
                            })}
                        </div>}
                    </div>
                ) : (
                    <div className="nx-consent__pane">
                        <PreviewPane load={loadPreview} access={realm.ceilingBlob}
                            empty={`None of ${w.your} nodes: this realm covers none of ${w.inGroup ? 'its' : 'your'} data.`} />
                    </div>
                )}
            </ConsentFrame>

        </Inspector>
    );
}
